/*
 * Copyright (c) 2020 Via Transportation, Inc. All rights reserved.
 *
 * This software is distributed under license and may not be used, copied,
 * modified, or distributed without the express written permission of
 * Via Transportation, Inc.
 */

import mixpanel from "mixpanel-browser";
import { UAParser } from "ua-parser-js";

import AnalyticsUserIdentity from "common/analytics/types/AnalyticsUserIdentities";
import { convertKeysToSnakeCase, replacer } from "common/util";

const getDistinctId = ({ cityId, driverId, tenantId }: AnalyticsUserIdentity) =>
    `driver_${tenantId}_${cityId}_${driverId}`;

class AnalyticsService {
    private userIdentity: AnalyticsUserIdentity | null = null;

    constructor(token: string, apiHost?: string) {
        mixpanel.init(token, {
            debug: process.env.NODE_ENV !== "production",
            persistence: "localStorage",
            track_pageview: false,
            ...(apiHost ? { api_host: apiHost } : {}),
        });
    }

    identify = (userIdentity?: AnalyticsUserIdentity) => {
        if (userIdentity) {
            this.userIdentity = userIdentity;
            mixpanel.identify(getDistinctId(userIdentity));
        } else {
            this.userIdentity = null;
            mixpanel.reset();
        }
    };

    logEvent = (name: string, info: any = {}) => {
        // Use the identity verbatim so `driver_id` matches the distinct id and never
        // becomes NaN for non-numeric driver ids.
        const userIdentity = this.userIdentity ?? {};
        const ua = new UAParser(navigator.userAgent);
        const device = ua.getDevice();
        const os = ua.getOS();
        const platform =
            device.type === "mobile" || device.type === "tablet" ? "mobile" : "desktop";

        info.loginSource = window.localStorage.getItem("isDriverApp") ? "driver_app" : "browser";

        mixpanel.track(
            name,
            convertKeysToSnakeCase({
                ...userIdentity,
                ...Object.keys(info).reduce(
                    (acc, val) => ({
                        ...acc,
                        [val]:
                            typeof info[val] === "object"
                                ? JSON.stringify(info[val], replacer)
                                : info[val],
                    }),
                    {} as Record<string, any>
                ),
                appVersion: process.env.REACT_APP_GIT_COMMIT_TAG || "",
                deviceModel: device.model || "",
                deviceOs: os.name,
                eventTs: Date.now(),
                localeLanguage: navigator.language,
                platform,
                screenSize: JSON.stringify(
                    {
                        height: window.screen.height,
                        width: window.screen.width,
                    },
                    replacer
                ),
            })
        );
    };
}

let instance: AnalyticsService;

export const getAnalyticsServiceInstance = (token?: string, apiHost?: string) => {
    if (!instance && token) {
        instance = new AnalyticsService(token, apiHost);
    }
    return instance;
};
